import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Link, useLocalSearchParams, useRouter } from 'expo-router';

import {
  matchImportTransfers,
  mergePreservingEdits,
  shiftIsoDate,
  TRANSFER_PAIR_WINDOW_DAYS,
  type TransferChoice,
} from '@couplespace/core/finance/import';

import { Button, Dialog, DialogButton, Text } from '@/components/ui';
import { fetchAccounts, fetchCategories, fetchTransactionsInRange } from '@/lib/finance/api';
import { ensureCategoriesFromLabels } from '@/lib/finance/ensure-categories';
import { accountPickerLabel, categoryOptions, formatAmount } from '@/lib/finance/format';
import { splitCategoryLabel } from '@/lib/finance/import';
import type { Account, Category, FinanceCategoryKind, Transaction } from '@/lib/finance/types';
import {
  applyMerchantCategoryPicks,
  cancelImportBatch,
  commitImportBatch,
  duplicateNote,
  exactDescriptionKey,
  fetchImportBatch,
  pickImportAccount,
  reparseImportBatch,
  sameDescriptionRowIndexes,
  signedImportCommitAmount,
  transferCommitLegs,
  type CommitImportRowInput,
  type FinanceImportBatch,
  type FinanceImportBatchRow,
  type MerchantCategoryPick,
} from '@/lib/finance/smart-import';

function toCommitRow(
  row: FinanceImportBatchRow,
  choice: TransferChoice | null,
  transferCategoryId: number | null,
): CommitImportRowInput | null {
  if (!row.date || row.amount == null || !row.currencyCode) return null;
  if (choice) {
    const legs = transferCommitLegs({
      statementAccountId: row.accountId,
      amount: row.amount,
      fromAccountId: choice.fromAccountId,
      toAccountId: choice.toAccountId,
      linkedTransactionId: choice.linkedTransactionId,
    });
    return {
      rowIndex: row.rowIndex,
      selected: row.selected,
      date: row.date,
      amount: legs.amount,
      currencyCode: row.currencyCode,
      accountId: legs.accountId,
      toAccountId: legs.toAccountId,
      linkedTransactionId: choice.linkedTransactionId,
      categoryId: transferCategoryId ?? row.categoryId,
      notes: row.notes,
    };
  }
  return {
    rowIndex: row.rowIndex,
    selected: row.selected,
    date: row.date,
    amount: row.amount,
    currencyCode: row.currencyCode,
    accountId: row.accountId,
    toAccountId: null,
    categoryId: row.categoryId,
    notes: row.notes,
  };
}

function preferredTransferCategoryId(categories: Category[]): number | null {
  const transfers = categories.filter(
    (category) => category.kind === 'transfer' && category.isActive,
  );
  const named = transfers.find(
    (category) => category.parentId == null && category.name.toLowerCase() === 'transfer',
  );
  return (
    (named ?? transfers.find((category) => category.parentId == null) ?? transfers[0])?.id ?? null
  );
}

function accountLabel(accounts: Account[], id: number): string {
  const account = accounts.find((item) => item.id === id);
  return account ? accountPickerLabel(account) : 'Account';
}

function transferCaption(
  choice: TransferChoice,
  statementAccountId: number,
  accounts: Account[],
): string {
  const inbound = choice.toAccountId === statementAccountId;
  const otherId = inbound ? choice.fromAccountId : choice.toAccountId;
  const verb = inbound ? 'Transfer from' : 'Transfer to';
  const label = `${verb} ${accountLabel(accounts, otherId)}`;
  if (choice.linkedTransactionId == null) return label;
  if (choice.dayGap === 1) return `${label} · matches an existing transaction one day apart`;
  if (choice.dayGap > 1) {
    return `${label} · matches an existing transaction ${choice.dayGap} days apart`;
  }
  return `${label} · matches an existing transaction on the same day`;
}

function displayDirection(
  row: FinanceImportBatchRow,
  categoryKind: FinanceCategoryKind | undefined,
): 'expense' | 'income' | 'transfer' {
  if (row.direction === 'income' || row.direction === 'expense' || row.direction === 'transfer') {
    return row.direction;
  }
  if (categoryKind === 'income' || categoryKind === 'expense' || categoryKind === 'transfer') {
    return categoryKind;
  }
  return 'expense';
}

function rowSkipSummary(error: string): boolean {
  const parts = error
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length > 0 && parts.every((part) => /^Row \d+:/.test(part));
}

function signedAmount(row: FinanceImportBatchRow, categoryKind?: FinanceCategoryKind): string {
  if (row.amount == null) return '—';
  const sign = displayDirection(row, categoryKind) === 'income' ? '+' : '−';
  return `${sign}${formatAmount(row.amount)} ${row.currencyCode ?? ''}`.trim();
}

function categoryKindForRow(row: FinanceImportBatchRow | undefined): FinanceCategoryKind {
  if (row?.direction === 'income' || row?.direction === 'transfer') return row.direction;
  return 'expense';
}

export function ImportReview() {
  const router = useRouter();
  const params = useLocalSearchParams<{ batchId?: string }>();
  const batchId = params.batchId ?? '';

  const [batch, setBatch] = useState<FinanceImportBatch | null>(null);
  const [rows, setRows] = useState<FinanceImportBatchRow[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [ledger, setLedger] = useState<Transaction[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());
  const [overrides, setOverrides] = useState<Map<number, TransferChoice>>(() => new Map());
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [createRowIndex, setCreateRowIndex] = useState<number | null>(null);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [bulkPrompt, setBulkPrompt] = useState<{
    rowIndex: number;
    patch: Partial<FinanceImportBatchRow>;
    kind: FinanceCategoryKind | undefined;
    keptDirection: 'expense' | 'income' | undefined;
    others: number[];
    notes: string;
    direction: FinanceImportBatchRow['direction'];
  } | null>(null);
  const appliedAccountRef = useRef(false);
  const dirtyRef = useRef(new Set<number>());
  const directionOverrideRef = useRef(new Map<number, 'expense' | 'income'>());
  const merchantPicksRef = useRef(new Map<string, MerchantCategoryPick>());
  const accountIdRef = useRef<number | null>(null);
  accountIdRef.current = accountId;
  const loggedKeyRef = useRef('');
  const transferCategoryFailedRef = useRef(false);
  const creatingTransferRef = useRef(false);
  const selectionTouchedRef = useRef(new Set<number>());

  const categoryOpts = useMemo(() => categoryOptions(categories), [categories]);
  const transferCategoryId = useMemo(() => preferredTransferCategoryId(categories), [categories]);
  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const da = a.date ?? '';
        const db = b.date ?? '';
        if (da !== db) return da.localeCompare(db);
        return a.rowIndex - b.rowIndex;
      }),
    [rows],
  );

  const loadMasters = useCallback(async () => {
    const [accountRows, categoryRows] = await Promise.all([fetchAccounts(), fetchCategories()]);
    setAccounts(accountRows);
    setCategories(categoryRows);
  }, []);

  const reloadBatch = useCallback(async () => {
    if (!batchId) return;
    setError(null);
    try {
      const batchRow = await fetchImportBatch(batchId);
      setBatch(batchRow);
      setRows((current) => {
        const merged = applyMerchantCategoryPicks(
          mergePreservingEdits(current, batchRow.rows ?? [], dirtyRef.current),
          merchantPicksRef.current,
          dirtyRef.current,
        );
        const picked = appliedAccountRef.current ? accountIdRef.current : null;
        if (picked == null) return merged;
        return merged.map((row) =>
          dirtyRef.current.has(row.rowIndex) ? row : { ...row, accountId: picked },
        );
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load import');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    appliedAccountRef.current = false;
    dirtyRef.current = new Set();
    directionOverrideRef.current = new Map();
    merchantPicksRef.current = new Map();
    setBulkPrompt(null);
    setDismissed(new Set());
    setOverrides(new Map());
    setLedger([]);
    transferCategoryFailedRef.current = false;
    creatingTransferRef.current = false;
    selectionTouchedRef.current = new Set();
  }, [batchId]);

  useEffect(() => {
    void loadMasters();
  }, [loadMasters]);

  useEffect(() => {
    void reloadBatch();
  }, [reloadBatch]);

  useEffect(() => {
    if (
      !batch ||
      batch.status === 'ready' ||
      batch.status === 'failed' ||
      batch.status === 'cancelled'
    )
      return;
    const timer = window.setInterval(() => {
      void reloadBatch();
    }, 2000);
    return () => window.clearInterval(timer);
  }, [batch, reloadBatch]);

  useEffect(() => {
    if (batch?.status === 'pending' || batch?.status === 'parsing') {
      appliedAccountRef.current = false;
    }
  }, [batch?.status]);

  useEffect(() => {
    if (!batch) return;
    const progress = batch.stats?.progress;
    const key = [
      batch.status,
      progress?.phase ?? '',
      progress?.detail ?? '',
      batch.error ?? '',
      String(batch.rows?.length ?? batch.stats?.totalRows ?? 0),
    ].join('|');
    if (loggedKeyRef.current === key) return;
    loggedKeyRef.current = key;
    console.info('[CS-FINANCE-IMPORT]', {
      batchId: batch.id,
      status: batch.status,
      filename: batch.filename,
      sourceLabel: batch.sourceLabel,
      model: batch.model,
      phase: progress?.phase ?? null,
      detail: progress?.detail ?? null,
      error: batch.error,
      rows: batch.rows?.length ?? batch.stats?.totalRows ?? 0,
    });
  }, [batch]);

  useEffect(() => {
    if (batch?.status !== 'ready' && batch?.status !== 'parsing') return;
    if (accounts.length === 0 || rows.length === 0) return;
    if (!appliedAccountRef.current) {
      const picked = pickImportAccount(accounts, rows);
      if (picked == null) {
        if (batch?.status !== 'parsing') appliedAccountRef.current = true;
        return;
      }
      appliedAccountRef.current = true;
      setAccountId(picked);
      setRows((current) =>
        current.map((row) =>
          dirtyRef.current.has(row.rowIndex) || row.accountId != null
            ? row
            : { ...row, accountId: picked },
        ),
      );
      return;
    }
    if (accountId == null) return;
    setRows((current) => {
      let changed = false;
      const next = current.map((row) => {
        if (dirtyRef.current.has(row.rowIndex) || row.accountId != null) return row;
        changed = true;
        return { ...row, accountId };
      });
      return changed ? next : current;
    });
  }, [batch?.status, accounts, rows, accountId]);

  const dateSpan = useMemo(() => {
    const dates = (batch?.rows ?? [])
      .map((row) => row.date)
      .filter((date): date is string => Boolean(date));
    if (dates.length === 0) return null;
    const min = dates.reduce((left, right) => (left < right ? left : right));
    const max = dates.reduce((left, right) => (left > right ? left : right));
    return {
      from: shiftIsoDate(min, -TRANSFER_PAIR_WINDOW_DAYS),
      to: shiftIsoDate(max, TRANSFER_PAIR_WINDOW_DAYS),
    };
  }, [batch?.rows]);

  useEffect(() => {
    if (!dateSpan || (batch?.status !== 'ready' && batch?.status !== 'parsing')) return;
    let cancelled = false;
    void fetchTransactionsInRange(dateSpan.from, dateSpan.to)
      .then((transactions) => {
        if (!cancelled) setLedger(transactions);
      })
      .catch((err) => {
        console.info('[CS-FINANCE-IMPORT] transfer lookup failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [dateSpan, batch?.status]);

  const legs = useMemo(
    () =>
      ledger.map((transaction) => ({
        id: transaction.id,
        date: transaction.date,
        amount: transaction.amount,
        currencyCode: transaction.currencyCode,
        accountId: transaction.accountId,
        toAccountId: transaction.toAccountId,
        kind:
          categories.find((category) => category.id === transaction.categoryId)?.kind ??
          (transaction.toAccountId != null ? ('transfer' as const) : ('expense' as const)),
      })),
    [ledger, categories],
  );

  const matchResult = useMemo(() => {
    if (accountId == null)
      return {
        suggestions: [],
        ambiguous: [] as { rowIndex: number; choices: TransferChoice[] }[],
      };
    return matchImportTransfers({
      sourceAccountId: accountId,
      rows,
      accounts,
      legs,
    });
  }, [accountId, rows, accounts, legs]);

  useEffect(() => {
    const pairIndexes = new Set(
      matchResult.suggestions
        .filter((item) => item.linkedTransactionId != null)
        .map((item) => item.rowIndex),
    );
    if (pairIndexes.size === 0) return;
    setRows((current) => {
      let changed = false;
      const next = current.map((row) => {
        if (!pairIndexes.has(row.rowIndex) || row.selected || row.duplicateMatch === 'exact') {
          return row;
        }
        if (selectionTouchedRef.current.has(row.rowIndex)) return row;
        changed = true;
        return { ...row, selected: true };
      });
      return changed ? next : current;
    });
  }, [matchResult.suggestions]);

  useEffect(() => {
    if (
      transferCategoryId != null ||
      transferCategoryFailedRef.current ||
      creatingTransferRef.current
    ) {
      return;
    }
    const needs =
      matchResult.suggestions.length > 0 || matchResult.ambiguous.length > 0 || overrides.size > 0;
    if (!needs) return;
    creatingTransferRef.current = true;
    let cancelled = false;
    void ensureCategoriesFromLabels(['Transfer'], categories, 'transfer')
      .then((created) => {
        if (!cancelled) setCategories(created.categories);
      })
      .catch((err) => {
        transferCategoryFailedRef.current = true;
        console.info('[CS-FINANCE-IMPORT] transfer category create failed', err);
      })
      .finally(() => {
        creatingTransferRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [
    transferCategoryId,
    matchResult.suggestions.length,
    matchResult.ambiguous.length,
    overrides.size,
    categories,
  ]);

  function choiceFor(rowIndex: number): TransferChoice | null {
    if (dismissed.has(rowIndex)) return null;
    return (
      overrides.get(rowIndex) ??
      matchResult.suggestions.find((item) => item.rowIndex === rowIndex) ??
      null
    );
  }

  function ambiguousFor(rowIndex: number): TransferChoice[] | null {
    if (dismissed.has(rowIndex) || overrides.has(rowIndex)) return null;
    if (matchResult.suggestions.some((item) => item.rowIndex === rowIndex)) return null;
    return matchResult.ambiguous.find((item) => item.rowIndex === rowIndex)?.choices ?? null;
  }

  function dismissTransfer(rowIndex: number) {
    setDismissed((current) => new Set(current).add(rowIndex));
    setOverrides((current) => {
      const next = new Map(current);
      next.delete(rowIndex);
      return next;
    });
    const row = rows.find((item) => item.rowIndex === rowIndex);
    if (row && transferCategoryId != null && row.categoryId === transferCategoryId) {
      updateRow(rowIndex, { categoryId: null });
    }
  }

  const selectedCount = rows.filter((row) => row.selected && row.errors.length === 0).length;
  const duplicateCount = rows.filter((row) => row.duplicateOfTransactionId != null).length;
  const transferCount = rows.filter(
    (row) => row.selected && choiceFor(row.rowIndex) != null,
  ).length;
  const linkCount = rows.filter(
    (row) => row.selected && choiceFor(row.rowIndex)?.linkedTransactionId != null,
  ).length;

  function updateRow(rowIndex: number, patch: Partial<FinanceImportBatchRow>) {
    dirtyRef.current.add(rowIndex);
    setRows((current) =>
      current.map((row) => (row.rowIndex === rowIndex ? { ...row, ...patch } : row)),
    );
  }

  function flipDirection(rowIndex: number) {
    const row = rows.find((item) => item.rowIndex === rowIndex);
    if (!row || row.amount == null) return;
    const choice = choiceFor(rowIndex);
    const categoryKind = categoryOpts.find((option) => option.id === row.categoryId)?.kind;
    const current = displayDirection(row, choice ? 'transfer' : categoryKind);
    const next = current === 'income' ? 'expense' : 'income';
    directionOverrideRef.current.set(rowIndex, next);
    if (choice) {
      setOverrides((currentMap) => {
        const nextMap = new Map(currentMap);
        nextMap.set(rowIndex, {
          ...choice,
          fromAccountId: choice.toAccountId,
          toAccountId: choice.fromAccountId,
        });
        return nextMap;
      });
      setDismissed((currentSet) => {
        if (!currentSet.has(rowIndex)) return currentSet;
        const nextSet = new Set(currentSet);
        nextSet.delete(rowIndex);
        return nextSet;
      });
    }
    updateRow(rowIndex, { direction: next });
  }

  function writeCategory(
    rowIndex: number,
    patch: Partial<FinanceImportBatchRow>,
    kind: FinanceCategoryKind | undefined,
    targets: number[],
    keptDirection: 'expense' | 'income' | undefined,
  ) {
    const targetSet = new Set(targets);
    if (kind === 'transfer') {
      setDismissed((current) => {
        const next = new Set(current);
        for (const index of targetSet) next.delete(index);
        return next;
      });
    } else {
      setDismissed((current) => new Set([...current, ...targetSet]));
      setOverrides((current) => {
        const next = new Map(current);
        for (const index of targetSet) next.delete(index);
        return next;
      });
    }
    for (const index of targetSet) dirtyRef.current.add(index);
    setRows((current) =>
      current.map((row) => {
        if (!targetSet.has(row.rowIndex)) return row;
        if (row.rowIndex === rowIndex && keptDirection) {
          return { ...row, ...patch, direction: keptDirection };
        }
        return { ...row, ...patch };
      }),
    );
  }

  function applyCategory(rowIndex: number, nextId: number | null, knownKind?: FinanceCategoryKind) {
    const kind = knownKind ?? categoryOpts.find((option) => option.id === nextId)?.kind;
    const keptDirection = directionOverrideRef.current.get(rowIndex);
    const source = rows.find((row) => row.rowIndex === rowIndex);
    const transferDirection =
      keptDirection ?? (source?.direction === 'income' ? 'income' : 'expense');
    const patch: Partial<FinanceImportBatchRow> = {
      categoryId: nextId,
      ...(kind === 'transfer'
        ? { direction: transferDirection }
        : kind === 'income' || kind === 'expense'
          ? { direction: kind }
          : {}),
    };
    writeCategory(rowIndex, patch, kind, [rowIndex], keptDirection);
    const others = sameDescriptionRowIndexes(rows, rowIndex).filter((index) => index !== rowIndex);
    if (others.length === 0 || !source) return;
    setBulkPrompt({
      rowIndex,
      patch,
      kind,
      keptDirection,
      others,
      notes: source.notes?.trim() ?? '',
      direction: source.direction,
    });
  }

  function confirmBulkCategory() {
    if (!bulkPrompt) return;
    writeCategory(
      bulkPrompt.rowIndex,
      bulkPrompt.patch,
      bulkPrompt.kind,
      [bulkPrompt.rowIndex, ...bulkPrompt.others],
      bulkPrompt.keptDirection,
    );
    const key = exactDescriptionKey(bulkPrompt.notes);
    if (key) {
      merchantPicksRef.current.set(key, {
        direction: bulkPrompt.direction,
        patch: bulkPrompt.patch,
      });
    }
    setBulkPrompt(null);
  }

  function declineBulkCategory() {
    if (bulkPrompt) {
      const key = exactDescriptionKey(bulkPrompt.notes);
      if (key) merchantPicksRef.current.delete(key);
    }
    setBulkPrompt(null);
  }

  function applyAccount(nextAccountId: number | null) {
    appliedAccountRef.current = true;
    setAccountId(nextAccountId);
    setDismissed(new Set());
    setOverrides(new Map());
    setRows((current) => current.map((row) => ({ ...row, accountId: nextAccountId })));
  }

  function openCreateCategory(rowIndex: number) {
    setCreateRowIndex(rowIndex);
    setNewCategoryLabel('');
    setCreateError(null);
  }

  async function onCreateCategory() {
    if (createRowIndex == null) return;
    const label = newCategoryLabel.trim();
    if (!label) return;
    const row = rows.find((item) => item.rowIndex === createRowIndex);
    const { parent } = splitCategoryLabel(label);
    const existingParent = categories.find(
      (category) =>
        category.parentId == null && category.name.toLowerCase() === parent.toLowerCase(),
    );
    const kind = existingParent?.kind ?? categoryKindForRow(row);
    setCreatingCategory(true);
    setCreateError(null);
    try {
      const created = await ensureCategoriesFromLabels([label], categories, kind);
      const id = created.byLabel.get(label.toLowerCase());
      if (id == null) throw new Error('Category was not created');
      setCategories(created.categories);
      applyCategory(createRowIndex, id, kind);
      setCreateRowIndex(null);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create category');
    } finally {
      setCreatingCategory(false);
    }
  }

  async function onCancel() {
    if (!batch) return;
    setCancelling(true);
    setError(null);
    try {
      const updated = await cancelImportBatch(batch.id);
      setBatch(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  }

  async function onReparse() {
    if (!batch) return;
    setError(null);
    try {
      const updated = await reparseImportBatch(batch.id);
      dirtyRef.current = new Set();
      directionOverrideRef.current = new Map();
      merchantPicksRef.current = new Map();
      appliedAccountRef.current = false;
      setBatch(updated);
      setRows(updated.rows ?? []);
      setDismissed(new Set());
      setOverrides(new Map());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Re-parse failed');
    }
  }

  async function onCommit() {
    if (!batch) return;
    setCommitting(true);
    setError(null);
    try {
      const linkedIds = new Set<number>();
      for (const row of rows) {
        if (!row.selected) continue;
        const linkId = choiceFor(row.rowIndex)?.linkedTransactionId;
        if (linkId == null) continue;
        if (linkedIds.has(linkId)) {
          setError('Two rows match the same existing transaction. Clear one of them.');
          return;
        }
        linkedIds.add(linkId);
      }
      const payload = rows
        .map((row) => {
          const choice = choiceFor(row.rowIndex);
          const committed = toCommitRow(row, choice, transferCategoryId);
          if (!committed || choice) return committed;
          const direction = directionOverrideRef.current.get(row.rowIndex) ?? row.direction;
          const categoryKind = categoryOpts.find(
            (option) => option.id === committed.categoryId,
          )?.kind;
          return {
            ...committed,
            amount: signedImportCommitAmount(committed.amount, direction, categoryKind),
          };
        })
        .filter((row): row is CommitImportRowInput => row != null);
      await commitImportBatch(batch.id, payload);
      router.push('/finance/transactions');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setCommitting(false);
    }
  }

  if (loading && !batch) {
    return (
      <View className="items-center py-12">
        <ActivityIndicator />
      </View>
    );
  }

  if (!batch) {
    return <Text className="text-danger">{error ?? 'Import batch not found'}</Text>;
  }

  const stillParsing = batch.status === 'pending' || batch.status === 'parsing';
  if (stillParsing && rows.length === 0) {
    return (
      <View className="gap-3 py-8">
        <ActivityIndicator />
        <Text variant="body-sm" className="text-muted">
          Parsing {batch.sourceLabel || batch.filename || 'pasted text'}…
        </Text>
        {batch.stats?.progress?.detail ? (
          <Text variant="body-sm" className="text-muted">
            {batch.stats.progress.detail}
          </Text>
        ) : null}
        {error ? <Text className="text-danger">{error}</Text> : null}
        <View className="flex-row flex-wrap gap-2">
          <Button
            label={cancelling ? 'Cancelling…' : 'Cancel'}
            variant="secondary"
            disabled={cancelling}
            onPress={() => void onCancel()}
          />
          <Button
            label="Back to history"
            variant="secondary"
            onPress={() => router.push('/finance/transactions/smart-import')}
          />
        </View>
      </View>
    );
  }

  if (batch.status === 'failed' || batch.status === 'cancelled') {
    return (
      <View className="gap-3">
        <Text variant="h3">
          {batch.status === 'cancelled' ? 'Import cancelled' : 'Import failed'}
        </Text>
        <Text className={batch.status === 'cancelled' ? 'text-muted' : 'text-danger'}>
          {batch.error ??
            (batch.status === 'cancelled' ? 'Parsing was cancelled.' : 'Unknown error')}
        </Text>
        {error ? <Text className="text-danger">{error}</Text> : null}
        <View className="flex-row flex-wrap gap-2">
          <Button label="Re-parse" variant="secondary" onPress={() => void onReparse()} />
          <Button
            label="Back to history"
            onPress={() => router.push('/finance/transactions/smart-import')}
          />
        </View>
      </View>
    );
  }

  return (
    <View className="gap-4">
      <View className="flex-row flex-wrap items-center gap-3">
        <View className="min-w-0">
          <Text variant="h3">Review import</Text>
          <Text variant="body-sm" className="text-muted">
            {batch.sourceLabel || batch.filename || 'Pasted text'} ·{' '}
            {stillParsing
              ? `${rows.length} so far, still parsing`
              : `${rows.length} rows · ${duplicateCount} duplicates`}
          </Text>
          {batch.stats?.sheetNote ? (
            <Text variant="body-sm" className="text-muted">
              {batch.stats.sheetNote}
            </Text>
          ) : null}
        </View>
        <View className="flex-1" />
        <select
          aria-label="Account"
          className="cs-input h-8 min-w-44 py-0"
          value={accountId ?? ''}
          onChange={(event) => applyAccount(event.target.value ? Number(event.target.value) : null)}
        >
          <option value="">Account…</option>
          {accounts.map((account) => (
            <option key={account.id} value={String(account.id)}>
              {accountPickerLabel(account)}
            </option>
          ))}
        </select>
        <Button
          label="History"
          size="sm"
          variant="secondary"
          onPress={() => router.push('/finance/transactions/smart-import')}
        />
        <Button
          label={
            stillParsing ? 'Still parsing…' : committing ? 'Importing…' : `Import ${selectedCount}`
          }
          size="sm"
          variant="primary"
          disabled={committing || stillParsing || selectedCount === 0}
          onPress={() => void onCommit()}
        />
      </View>

      {stillParsing ? (
        <View className="gap-2 rounded border border-border px-3 py-2">
          <Text variant="body-sm">
            {rows.length} transactions are ready. Categorize them while the rest of the statement is
            still parsing.
          </Text>
          {batch.stats?.progress?.detail ? (
            <Text variant="body-sm" className="text-muted">
              {batch.stats.progress.detail}
            </Text>
          ) : null}
          <View className="flex-row">
            <Button
              label={cancelling ? 'Cancelling…' : 'Cancel'}
              size="sm"
              variant="secondary"
              disabled={cancelling}
              onPress={() => void onCancel()}
            />
          </View>
        </View>
      ) : null}

      {error ? <Text className="text-danger">{error}</Text> : null}
      {batch.error && batch.status === 'ready' && !rowSkipSummary(batch.error) ? (
        <Text className="text-danger">{batch.error}</Text>
      ) : null}

      <View className="overflow-x-auto rounded border border-border">
        <table className="w-full table-fixed border-collapse text-sm">
          <colgroup>
            <col className="w-10" />
            <col className="w-28" />
            <col className="w-36" />
            <col className="w-52" />
            <col />
          </colgroup>
          <thead>
            <tr className="bg-surface-raised text-left text-xs text-muted">
              <th className="px-2 py-1.5">Use</th>
              <th className="px-2 py-1.5">Date</th>
              <th className="px-2 py-1.5">Amount</th>
              <th className="px-2 py-1.5">Category</th>
              <th className="px-2 py-1.5">Notes</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const choice = choiceFor(row.rowIndex);
              const ambiguous = ambiguousFor(row.rowIndex);
              const paired =
                choice?.linkedTransactionId != null ||
                ambiguous?.some((item) => item.linkedTransactionId != null) === true;
              const isDuplicate = row.duplicateOfTransactionId != null && !paired;
              const shownCategoryId =
                choice && transferCategoryId != null ? transferCategoryId : row.categoryId;
              const shownKind = categoryOpts.find((option) => option.id === shownCategoryId)?.kind;
              const amountDirection = displayDirection(row, shownKind);
              const showManualAccount =
                !choice && ambiguous == null && shownKind === 'transfer' && accountId != null;
              return (
                <tr
                  key={row.rowIndex}
                  className={isDuplicate ? 'bg-surface-raised text-muted' : undefined}
                >
                  <td className="border-b border-border px-2 py-1 align-middle">
                    <input
                      type="checkbox"
                      checked={row.selected}
                      disabled={row.errors.length > 0}
                      onChange={(event) => {
                        selectionTouchedRef.current.add(row.rowIndex);
                        updateRow(row.rowIndex, { selected: event.target.checked });
                      }}
                    />
                  </td>
                  <td className="border-b border-border px-2 py-1 align-middle whitespace-nowrap">
                    {row.date ?? '—'}
                  </td>
                  <td className="border-b border-border px-2 py-1 align-middle whitespace-nowrap tabular-nums">
                    {row.amount == null ? (
                      signedAmount(row, shownKind)
                    ) : (
                      <button
                        type="button"
                        title={
                          shownKind === 'transfer'
                            ? amountDirection === 'income'
                              ? 'Mark as transfer to'
                              : 'Mark as transfer from'
                            : amountDirection === 'income'
                              ? 'Mark as withdrawal'
                              : 'Mark as deposit'
                        }
                        aria-label={
                          shownKind === 'transfer'
                            ? amountDirection === 'income'
                              ? 'Mark as transfer to'
                              : 'Mark as transfer from'
                            : amountDirection === 'income'
                              ? 'Mark as withdrawal'
                              : 'Mark as deposit'
                        }
                        className={`rounded px-1 hover:underline ${
                          amountDirection === 'income'
                            ? 'text-success'
                            : amountDirection === 'expense'
                              ? 'text-danger'
                              : ''
                        }`}
                        onClick={() => flipDirection(row.rowIndex)}
                      >
                        {signedAmount(row, shownKind)}
                      </button>
                    )}
                  </td>
                  <td className="border-b border-border px-2 py-1 align-middle">
                    <select
                      className="cs-input h-8 w-full py-0"
                      value={shownCategoryId ?? ''}
                      onChange={(event) => {
                        if (event.target.value === 'new') {
                          openCreateCategory(row.rowIndex);
                          return;
                        }
                        applyCategory(
                          row.rowIndex,
                          event.target.value ? Number(event.target.value) : null,
                        );
                      }}
                    >
                      <option value="">Uncategorized</option>
                      <option value="new">Create category…</option>
                      {categoryOpts.map((option) => (
                        <option key={option.id} value={String(option.id)}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="border-b border-border px-2 py-1 align-middle">
                    <input
                      className="cs-input h-8 w-full py-0"
                      value={row.notes ?? ''}
                      onChange={(event) => updateRow(row.rowIndex, { notes: event.target.value })}
                    />
                    {isDuplicate ? (
                      <Text variant="caption" className="text-muted">
                        {row.duplicateOf ? (
                          <Link href={`/finance/transactions/${row.duplicateOfTransactionId}`}>
                            {duplicateNote(row)}
                          </Link>
                        ) : (
                          duplicateNote(row)
                        )}
                      </Text>
                    ) : null}
                    {choice && accountId != null ? (
                      <Text variant="caption" className="text-muted">
                        {transferCaption(choice, accountId, accounts)}{' '}
                        <button
                          type="button"
                          className="underline"
                          onClick={() => dismissTransfer(row.rowIndex)}
                        >
                          Not a transfer
                        </button>
                      </Text>
                    ) : null}
                    {ambiguous && accountId != null ? (
                      <select
                        aria-label="Match transfer"
                        className="cs-input mt-1 h-8 w-full py-0"
                        value=""
                        onChange={(event) => {
                          const picked = ambiguous[Number(event.target.value)];
                          if (!picked) return;
                          setOverrides((current) => new Map(current).set(row.rowIndex, picked));
                          if (picked.linkedTransactionId != null) {
                            selectionTouchedRef.current.add(row.rowIndex);
                            updateRow(row.rowIndex, { selected: true });
                          }
                        }}
                      >
                        <option value="">Possible transfer…</option>
                        {ambiguous.map((item, index) => (
                          <option
                            key={`${item.fromAccountId}-${item.toAccountId}-${item.linkedTransactionId ?? 'new'}-${item.dayGap}`}
                            value={String(index)}
                          >
                            {transferCaption(item, accountId, accounts)}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {showManualAccount ? (
                      <select
                        aria-label={row.direction === 'income' ? 'From account' : 'To account'}
                        className="cs-input mt-1 h-8 w-full py-0"
                        value=""
                        onChange={(event) => {
                          const otherId = Number(event.target.value);
                          if (!otherId || accountId == null) return;
                          const income = row.direction === 'income';
                          setOverrides((current) =>
                            new Map(current).set(row.rowIndex, {
                              fromAccountId: income ? otherId : accountId,
                              toAccountId: income ? accountId : otherId,
                              linkedTransactionId: null,
                              confidence: 'manual',
                              dayGap: 0,
                            }),
                          );
                        }}
                      >
                        <option value="">
                          {row.direction === 'income' ? 'From account…' : 'To account…'}
                        </option>
                        {accounts
                          .filter((account) => account.isActive && account.id !== accountId)
                          .map((account) => (
                            <option key={account.id} value={String(account.id)}>
                              {accountPickerLabel(account)}
                            </option>
                          ))}
                      </select>
                    ) : null}
                    {row.errors.map((message) => (
                      <Text key={message} variant="caption" className="text-danger">
                        {message}
                      </Text>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </View>

      <View className="rounded border border-border px-3 py-2">
        <Text variant="body-sm">
          Selected {selectedCount} of {rows.length} rows
          {duplicateCount > 0 ? ` · ${duplicateCount} duplicates pre-unchecked` : ''}
          {transferCount > 0
            ? ` · ${transferCount} transfer${transferCount === 1 ? '' : 's'}${
                linkCount > 0
                  ? ` (${linkCount} link${linkCount === 1 ? 's' : ''} to an existing transaction)`
                  : ''
              }`
            : ''}
        </Text>
      </View>

      {(batch.rejectedRows?.length ?? 0) > 0 ? (
        <View className="gap-2">
          <Text variant="body-sm" className="font-medium">
            Not imported ({batch.rejectedRows?.length})
          </Text>
          <Text variant="body-sm" className="text-muted">
            These statement rows were left out of the preview.
          </Text>
          <View className="max-h-80 gap-3 overflow-y-auto rounded border border-border px-3 py-2">
            {batch.rejectedRows?.map((item) => (
              <View key={item.row}>
                <Text variant="body-sm" className="text-muted">
                  Row {item.row} · {item.reason}
                </Text>
                {item.raw ? <Text variant="body-sm">{item.raw}</Text> : null}
              </View>
            ))}
          </View>
        </View>
      ) : null}

      <Dialog
        open={bulkPrompt != null}
        title="Apply to matching rows?"
        onClose={declineBulkCategory}
        footer={
          <>
            <DialogButton label="Only this row" onPress={declineBulkCategory} />
            <DialogButton
              label={`Update the other ${bulkPrompt?.others.length ?? 0}`}
              variant="primary"
              onPress={confirmBulkCategory}
            />
          </>
        }
      >
        <Text variant="body-sm">
          {bulkPrompt
            ? `${bulkPrompt.others.length} other ${
                bulkPrompt.others.length === 1 ? 'row has' : 'rows have'
              } this exact description. Update ${
                bulkPrompt.others.length === 1 ? 'it' : 'them'
              } to the same category?`
            : ''}
        </Text>
        {bulkPrompt?.notes ? (
          <Text variant="body-sm" className="mt-2 text-muted">
            {bulkPrompt.notes.length > 140
              ? `${bulkPrompt.notes.slice(0, 140)}…`
              : bulkPrompt.notes}
          </Text>
        ) : null}
      </Dialog>

      <Dialog
        open={createRowIndex != null}
        title="Create category"
        onClose={() => {
          if (!creatingCategory) setCreateRowIndex(null);
        }}
        footer={
          <>
            <DialogButton
              label="Cancel"
              disabled={creatingCategory}
              onPress={() => setCreateRowIndex(null)}
            />
            <DialogButton
              label={creatingCategory ? 'Creating…' : 'Create'}
              variant="primary"
              disabled={creatingCategory || newCategoryLabel.trim() === ''}
              onPress={() => void onCreateCategory()}
            />
          </>
        }
      >
        <Text variant="body-sm" className="mb-2 text-muted">
          Type a name, or Parent - Child to nest it. Example: Food - Groceries.
        </Text>
        <input
          className="cs-input w-full"
          autoFocus
          value={newCategoryLabel}
          placeholder="Food - Groceries"
          disabled={creatingCategory}
          onChange={(event) => setNewCategoryLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void onCreateCategory();
          }}
        />
        {createError ? (
          <Text variant="body-sm" className="mt-2 text-danger">
            {createError}
          </Text>
        ) : null}
      </Dialog>
    </View>
  );
}
